// ============================================================================
// FRONT OFFICE CLOSING SERVICE (PMS Phase 2 slice 7d)
// ============================================================================
// Backend: FoClosingController — the desk's day (receipts, paid-outs, stays
// settled, cash), its close / verification / reopen, the day sheet and the
// Front Office Collection report.

import { get, post } from '../client/axiosClient';
import { buildEndpoint } from '../endpoints/base.endpoints';
import type { ReportFormat } from 'utils/reportFile';
import type { ApiResponse } from '../types';
import type { FoCloseRequest, FoCollectionReport, FoDay } from '../types/foClosing.types';

const BASE = '/FoClosing';
const day = (propertyId: string, date: string) => buildEndpoint(`${BASE}/${propertyId}/${date}`);

const unwrap = <T>(r: { data?: ApiResponse<T> }, fallback: string): T => {
  if (!r.data?.data) throw new Error(r.data?.message || fallback);
  return r.data.data;
};

export const foClosingService = {
  async getDay(propertyId: string, date: string): Promise<FoDay> {
    return unwrap(await get<ApiResponse<FoDay>>(day(propertyId, date)), 'The Front Office day did not load.');
  },
  async close(propertyId: string, date: string, request: FoCloseRequest): Promise<FoDay> {
    return unwrap(await post<ApiResponse<FoDay>>(`${day(propertyId, date)}/close`, request), 'The day was not closed.');
  },
  async verify(propertyId: string, date: string, remarks?: string): Promise<FoDay> {
    return unwrap(await post<ApiResponse<FoDay>>(`${day(propertyId, date)}/verify`, { remarks }), 'The day was not verified.');
  },
  async reopen(propertyId: string, date: string, reason: string): Promise<FoDay> {
    return unwrap(await post<ApiResponse<FoDay>>(`${day(propertyId, date)}/reopen`, { reason }), 'The day was not reopened.');
  },
  async daySheet(propertyId: string, date: string): Promise<Blob> {
    const r = await get<Blob>(`${day(propertyId, date)}/pdf`, { responseType: 'blob' });
    return r.data;
  },
  async getReport(propertyId: string, from: string, to: string): Promise<FoCollectionReport> {
    return unwrap(await get<ApiResponse<FoCollectionReport>>(buildEndpoint(`${BASE}/report`), { params: { propertyId, from, to } }),
      'The report did not load.');
  },
  async exportReport(propertyId: string, from: string, to: string, format: ReportFormat) {
    const r = await get<Blob>(buildEndpoint(`${BASE}/report/export`), { params: { propertyId, from, to, format }, responseType: 'blob' });
    return { blob: r.data, disposition: r.headers?.['content-disposition'] as string | undefined };
  },
};

export default foClosingService;
